import logging from 'utils/logging';

const log = logging.getLogger('ttsService');

// Test sound path (for stub implementation)
const TEST_SOUND_PATH = '/sounds/test_sound.mp3';

/**
 * Phase 1: Generate audio for narration text and store reference.
 * In the stub, this just returns the test sound path/URL.
 * 
 * @param {string} text - The narration text to convert to audio
 * @param {string} narrationKey - Unique key for this narration moment
 * @returns {Promise<{audioUrl: string, audioStorageKey: string, duration: number, meta: object}>}
 */
export async function generateAudio(text, narrationKey) {
  log.info(`Generating audio for narration: "${text.substring(0, 50)}${text.length > 50 ? '...' : ''}"`);
  
  // Simulate API call delay
  await new Promise(resolve => setTimeout(resolve, 200));
  
  // For the stub, always return the test sound
  const audioUrl = TEST_SOUND_PATH;
  // In a real implementation, this would be a path in Firebase Storage
  const audioStorageKey = `host_audio/${narrationKey}_${Date.now()}`;
  
  const result = {
    audioUrl,
    audioStorageKey,
    duration: 3, // seconds
    meta: {
      generatedAt: Date.now(),
      voice: 'stub-voice'
    }
  };
  
  log.info(`Generated audio for narration key ${narrationKey}:`, result);
  return result;
}

/**
 * Phase 2: Play the generated audio.
 * 
 * @param {string} audioUrl - URL to the audio file
 * @returns {Promise<{audio: HTMLAudioElement, promise: Promise}>} - Returns audio element and playback promise
 */
export async function playAudio(audioUrl) {
  log.info(`Playing audio from: ${audioUrl}`);
  
  try {
    const audio = new Audio(audioUrl);
    
    // Create a promise that resolves when audio completes or is interrupted
    const playbackPromise = new Promise((resolve, reject) => {
      audio.onended = resolve;
      audio.onerror = (error) => reject(new Error(`Audio playback failed: ${error.message}`));
      audio.addEventListener('interrupted', resolve);
    });
    
    // Start playing the audio
    await audio.play();
    
    log.info('Audio playback started');
    
    // Return both the audio element and the completion promise
    return {
      audio,
      promise: playbackPromise
    };
  } catch (error) {
    log.error('Error during audio playback:', error);
    throw error;
  }
}

/**
 * Interrupt an audio element by dispatching a custom event
 * 
 * @param {HTMLAudioElement} audio - The audio element to interrupt
 */
export function interruptAudio(audio) {
  if (audio) {
    // Dispatch a custom event that the promise can listen for
    audio.dispatchEvent(new Event('interrupted'));
  }
}

/**
 * Convenience method to both generate and play audio in one call
 * (for backward compatibility and simpler usage in some cases).
 * 
 * @param {string} text - The text to speak
 * @param {string} [narrationKey] - Optional key for the narration
 * @returns {Promise<void>}
 */
export async function speak(text, narrationKey = `adhoc_${Date.now()}`) {
  try {
    log.info(`Speaking text (combined generation and playback): "${text.substring(0, 50)}${text.length > 50 ? '...' : ''}"`);
    
    // Generate the audio
    const { audioUrl } = await generateAudio(text, narrationKey);
    
    // Play the audio
    await playAudio(audioUrl);
    
    log.info('Speak operation completed successfully');
  } catch (error) {
    log.error('Error in speak operation:', error);
    throw error;
  }
} 